import { makeAutoObservable, runInAction } from 'mobx';

import { FolderModel } from 'entities/folder/model/folderModel';
import { SectionModel } from 'entities/section/model/sectionModel';
import { sectionsApi } from 'entities/section/api/sectionsApi';
import { foldersApi } from 'entities/folder/api/foldersApi';
import { find, map } from 'lodash-es';
import { SelectItem } from 'shared/types/selectItem';
import { ExplorerEntityTypes } from '../../entities/types';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';

type EntityReference = {
  id: string | null;
  type: ExplorerEntityTypes.FOLDER | ExplorerEntityTypes.SECTION;
  name?: string;
  sectionId: string | null;
};

export class FolderSelectionVM {
  public currentEntity: EntityReference;
  private _folders: FolderModel[] = [];
  private _sections: SectionModel[] | null = null;
  public section: SectionModel | null = null;
  public folder: FolderModel | null = null;
  public selectedFolderId: string | null = null;
  public isCreateFolder: boolean = false;
  public isError = false;
  public isFolderPending = false;
  public isSectionPending = false;

  private filterFoldersById?: string[];

  constructor(initialEntity: EntityReference, filterFoldersById?: string[]) {
    this.currentEntity = initialEntity;
    this.filterFoldersById = filterFoldersById;
    makeAutoObservable(this);

    if (
      !this.currentEntity.name &&
      this.currentEntity.type === ExplorerEntityTypes.FOLDER &&
      this.currentEntity.id
    ) {
      this.fetchFolderName(this.currentEntity.id);
    }

    this.loadFolders();
    this.initSections();
  }

  public get parentId(): string | null {
    return this.currentEntity.type === ExplorerEntityTypes.SECTION
      ? null
      : this.currentEntity.id;
  }

  private async fetchFolderName(folderId: string) {
    try {
      const folder = await foldersApi.fetch(folderId, { in_trash: null });
      runInAction(() => {
        this.currentEntity.name = folder?.name || '';
      });
    } catch (error) {
      console.error('Ошибка загрузки имени папки:', error);
    }
  }

  // Загрузка секций
  private async initSections() {
    this.isSectionPending = true;
    try {
      const allSectionsData = await sectionsApi.fetchBulk();
      const allSections = map(
        allSectionsData,
        (section) => new SectionModel(section)
      );

      runInAction(() => {
        this._sections = allSections;
        this.isSectionPending = false;
        this._initializeCurrentSection();
      });
    } catch (e) {
      runInAction(() => {
        this.isSectionPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  // Установка текущей секции на основе выбранной
  private _initializeCurrentSection() {
    if (this.currentEntity.type === ExplorerEntityTypes.SECTION) {
      const initialSection = find(
        this._sections,
        (section) => section.id === this.currentEntity.id
      );
      if (initialSection) {
        this.section = initialSection;
      }
    } else if (this.currentEntity.type === ExplorerEntityTypes.FOLDER) {
      const folderSection = find(
        this._sections,
        (section) => section.id === this.currentEntity.sectionId
      );
      if (folderSection) {
        this.section = folderSection;
      }
    }
  }

  // Выбор секции
  public selectSection(newId: string) {
    const selectedSection =
      find(this._sections, (section) => section.id === newId) || null;
    this.section = selectedSection;
    this.setCurrentEntity({
      id: newId,
      sectionId: null,
      type: ExplorerEntityTypes.SECTION,
    });
    this._unselectFolder();
  }

  private _unselectFolder() {
    this.selectedFolderId = null;
    this.folder = null;
    this.isCreateFolder = false;
  }

  public unselectCurrentFolder() {
    if (this.currentEntity.type === ExplorerEntityTypes.FOLDER) {
      this.selectedFolderId = this.currentEntity.id;
      this.folder = null;
    } else {
      this._unselectFolder();
    }
    this.isCreateFolder = false;
  }

  // Выбор папки
  public selectFolder(folderId: string) {
    this.selectedFolderId = folderId;
    const selectedFolder = find(
      this._folders,
      (folder) => folder.id === folderId
    );
    if (selectedFolder) {
      this.folder = selectedFolder;
    }
    this.isCreateFolder = false;
  }

  // Переход в папку
  public navigateToFolder(folderId: string) {
    const selectedFolder = find(
      this._folders,
      (folder) => folder.id === folderId
    );
    if (selectedFolder) {
      this.setCurrentEntity({
        id: selectedFolder.id,
        sectionId: null,
        type: ExplorerEntityTypes.FOLDER,
        name: selectedFolder.name || '',
      });
      this.selectedFolderId = folderId;
    }
    this.isCreateFolder = false;
  }

  private setCurrentEntity(newEntity: EntityReference) {
    this.currentEntity = newEntity;
    this.folder = null;
    this.loadFolders();
    this.initSections();
  }

  // Загрузка папок
  private async loadFolders() {
    if (this._shouldSkipFolderLoad()) return;
    this.isFolderPending = true;
    try {
      const folders = await this._fetchFoldersForCurrentEntity();

      if (folders) {
        runInAction(() => {
          this._folders = this._filterFolders(folders);
          this._trySetDefaultSelectedFolder();
          this.isFolderPending = false;
        });
      }
    } catch (error) {
      runInAction(() => {
        this.isFolderPending = false;
        this.isError = true;
      });
      console.error('Ошибка загрузки папок:', error);
    }
  }

  // Проверка, нужно ли пропускать загрузку папок
  private _shouldSkipFolderLoad(): boolean {
    return (
      this.currentEntity.type === ExplorerEntityTypes.SECTION &&
      (!this.currentEntity.id || this.currentEntity.id === emptySelectItemId)
    );
  }

  // Загрузка папок для currentEntity
  private async _fetchFoldersForCurrentEntity() {
    if (this.currentEntity.id) {
      return this.currentEntity.type === ExplorerEntityTypes.FOLDER
        ? await foldersApi.fetchFolders(this.currentEntity.id, {
            in_trash: null,
          })
        : await sectionsApi.fetchFolders(this.currentEntity.id, {
            in_trash: null,
          });
    }

    console.warn('У текущей сущности нет id');
    return null;
  }

  // Фильтр папок
  private _filterFolders(folders: any[]): FolderModel[] {
    let filteredFolders = folders.map(
      (folderData) => new FolderModel(folderData)
    );
    if (this.filterFoldersById) {
      filteredFolders = filteredFolders.filter(
        (folder) => !this.filterFoldersById!.includes(folder.id)
      );
    }
    return filteredFolders;
  }

  // Установка выбранной папки по умолчанию
  private _trySetDefaultSelectedFolder() {
    if (
      !this.selectedFolderId &&
      this.currentEntity.type === ExplorerEntityTypes.FOLDER
    ) {
      this.selectedFolderId = this.currentEntity.id;
    }
  }

  //Переход на уровень выше
  public async goBack() {
    if (
      this.currentEntity.type === ExplorerEntityTypes.FOLDER &&
      this.currentEntity.id
    ) {
      const currentFolder = new FolderModel(this.currentEntity.id);
      await currentFolder.fetch({ in_trash: null });

      const parentId = currentFolder.data?.parent_id;
      if (parentId) {
        const parentFolder = new FolderModel(parentId);
        await parentFolder.fetch({ in_trash: null });
        const parentName = parentFolder.data?.name;
        this.setCurrentEntity({
          id: parentId,
          sectionId: null,
          type: ExplorerEntityTypes.FOLDER,
          name: parentName || '',
        });
        this.selectedFolderId = parentId;
      } else if (currentFolder.sectionId) {
        this.setCurrentEntity({
          id: currentFolder.sectionId,
          sectionId: null,
          type: ExplorerEntityTypes.SECTION,
        });
        this.selectedFolderId = null;
      }
    }
    this.isCreateFolder = false;
  }

  // Добавление папки
  public addFolder(newFolderModel: FolderModel) {
    if (this.folder) {
      this.folder.folders?.push(newFolderModel);
    } else if (this.section) {
      this.section.localAddFolder(newFolderModel);
    }
    this._folders = [newFolderModel, ...this._folders];

    this.selectedFolderId = newFolderModel.id;
    this.folder = newFolderModel;
  }

  // массив секций для селектора
  public get sectionsAsItems(): SelectItem[] {
    return map(this._sections, (section) => ({
      id: section.id,
      name: section?.data?.name || '',
    }));
  }

  // массив папок для селектора
  public get foldersAsItems(): SelectItem[] {
    return map(this._folders, (folder) => ({
      id: folder.id,
      name: folder?.data?.name || '',
    }));
  }

  // возможность навигации на уровень выше
  public get canNavigateUp(): boolean {
    return this.currentEntity.type === ExplorerEntityTypes.FOLDER;
  }
}
